import { tFailureCodeFilterValue } from 'src/pages/WhatsappApi/constants/FailureGroups.constants';
import { AxiosService } from '../../axios/axios.service';
import {
  tCreateTemplateRequest,
  tCreateTemplateResponse,
  tCreateCampaignRequest,
  tCreateDraftCampaignRequest,
  tCreateCampaignResponse,
  tCreateDraftCampaignResponse,
  tCampaignAtRiskPreviewRequest,
  tGetCampaignAtRiskPreviewResponse,
  tCampaignMissingVariablesPreviewRequest,
  tGetCampaignMissingVariablesPreviewResponse,
  tGetAccountResponse,
  tGetCampaignsResponse,
  tGetCampaignsStatisticsResponse,
  tCampaignMetricsResponse,
  tGetCreditsResponse,
  tGetDetailedStatisticsResponse,
  tGetFunnelsSummaryResponse,
  tGetNotificationsResponse,
  tGetStatisticsResponse,
  tGetUnreadCountResponse,
  tGetUndeliveredLeadsResponse,
  tNotificationCategory,
  tGetCampaignRecordResponse,
  tLeadsAudience,
  tTagsWithCountResponse,
} from 'src/pages/WhatsappApi/WhatsappApi.types.ts';
import {
  tGetCampaignDetailResponse,
  tResendCampaignRequest,
  tResendCampaignResponse,
  tDeleteCampaignResponse,
  tCancelCampaignResponse,
} from 'src/pages/WhatsappApi/components/CampaignsSection/components/CampaignDetailModal/CampaignDetailModal.types.ts';
import { tFetchTemplatesResponse } from 'src/pages/Pipe/Pipe.types.ts';

export const WhatsappApiService = {
  /**
   * Creates a new WhatsApp template for the specified workspace.
   * @param workspaceId Workspace ID.
   * @param request Data for the template to create.
   * @returns A promise that resolves with the template creation response.
   */
  createNewTemplate: async function (
    workspaceId: string,
    request: tCreateTemplateRequest,
  ) {
    const config = {
      headers: {},
      isLegacy: true,
    };

    try {
      const response = await AxiosService.post<
        tCreateTemplateRequest,
        tCreateTemplateResponse
      >(`/whatsapp-api/meta/${workspaceId}/templates`, request, config);
      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },

  /**
   * Updates an existing WhatsApp template for the specified workspace.
   * @param workspaceId Workspace ID.
   * @param templateName Name of the template to update.
   * @param request Data for the template to update.
   * @returns A promise that resolves with the updated template response.
   */
  updateTemplate: async function (
    workspaceId: string,
    templateName: string,
    request: Omit<tCreateTemplateRequest, 'category'>,
  ): Promise<tCreateTemplateResponse> {
    const config = {
      headers: {},
      isLegacy: true,
    };

    try {
      const response = await AxiosService.post<
        Omit<tCreateTemplateRequest, 'category'>,
        tCreateTemplateResponse
      >(
        `/whatsapp-api/meta/${workspaceId}/templates/${templateName}`,
        request,
        config,
      );
      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },

  /**
   * Gets the WhatsApp templates for the specified workspace.
   * @param workspaceId Workspace ID.
   * @returns A promise that resolves with the fetched templates.
   */
  getTemplates: async (workspaceId: string): Promise<tFetchTemplatesResponse> => {
    const options = {
      isLegacy: true,
    };

    try {
      const response = await AxiosService.get<tFetchTemplatesResponse>(
        `/whatsapp-api/meta/${workspaceId}/templates?all=true`,
        options,
      );

      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },

  /**
   * Deletes a WhatsApp template for the specified workspace.
   * @param workspaceId Workspace ID.
   * @param templateName Name of the template to delete.
   * @returns A promise that resolves when the template is deleted.
   */
  deleteTemplate: async (
    workspaceId: string,
    templateName: string,
  ): Promise<void> => {
    const options = {
      isLegacy: true,
    };

    try {
      await AxiosService.delete(
        `/whatsapp-api/meta/${workspaceId}/templates/${templateName}`,
        options,
      );
      return Promise.resolve();
    } catch (error) {
      return Promise.reject(error);
    }
  },

  /**
   * Fetches WhatsApp templates for the specified workspace.
   * @param workspaceId Workspace ID.
   * @returns A promise that resolves with the fetched templates.
   */
  fetchTemplates: async (
    workspaceId: string,
  ): Promise<tFetchTemplatesResponse> => {
    try {
      const options = {
        isLegacy: true,
      };

      const response = await AxiosService.post<
        // eslint-disable-next-line @typescript-eslint/no-empty-object-type
        {},
        tFetchTemplatesResponse
      >(`/whatsapp-api/meta/${workspaceId}/templates/fetch`, {}, options);

      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },
  /**
   * Get template credits usage for the specified workspace.
   * @param workspaceId Workspace ID.
   * @returns A promise that resolves with the template credits usage.
   */
  getTemplateCreditsUsage: async (
    workspaceId: string,
  ): Promise<tGetCreditsResponse> => {
    try {
      const options = {
        isLegacy: true,
      };

      const response = await AxiosService.get<tGetCreditsResponse>(
        `/billing/workspaces/${workspaceId}/feature-usages/feature-code/template_credits_limit/with-usage`,
        options,
      );

      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },
  /**
   * Get the statistics of sent messages for the specified workspace.
   * @param workspaceId Workspace ID.
   * @returns A promise that resolves with the sent messages statistics.
   */
  getSentMessagesStatistics: async (
    workspaceId: string,
    startDate?: string,
    endDate?: string,
  ): Promise<tGetStatisticsResponse> => {
    try {
      // INT-958: routed through bff-web (no isLegacy). Query forwarded verbatim
      // to WBS; both dates are sent together or not at all, as before.
      const params = new URLSearchParams();
      if (startDate && endDate) {
        params.set('start_date', startDate);
        params.set('end_date', endDate);
      }
      const query = params.toString();
      const url = `/whatsapp-api/workspaces/${workspaceId}/messaging/statistics${query ? `?${query}` : ''}`;

      const response = await AxiosService.get<tGetStatisticsResponse>(url);

      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },
  /**
   * Get Account data
   * @param workspaceId Workspace ID.
   * @returns A promise that resolves with the account data.
   */
  getAccountData: async (workspaceId: string): Promise<tGetAccountResponse> => {
    try {
      const options = {
        isLegacy: true,
      };
      const response = await AxiosService.get<tGetAccountResponse>(
        `/whatsapp-api/account/${workspaceId}`,
        options,
      );
      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },
  getCampaignsData: async (
    workspaceId: string,
    page: number,
    size?: number,
    name?: string,
  ): Promise<tGetCampaignsResponse> => {
    try {
      const options = {
        isLegacy: false,
      };
      const params = new URLSearchParams();
      params.set('pageNumber', String(page));
      if (size != null) params.set('pageSize', String(size));
      if (name) params.set('name', name);
      const url = `/whatsapp-api/workspaces/${workspaceId}/campaigns/summary?${params.toString()}`;
      const response = await AxiosService.get<tGetCampaignsResponse>(
        url,
        options,
      );
      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },
  getTemplatesByPage: async (
    workspaceId: string,
    pageNumber: number,
    pageSize: number,
    name?: string,
  ): Promise<tFetchTemplatesResponse> => {
    const options = { isLegacy: true };
    try {
      const params = new URLSearchParams();
      params.set('all', 'true');
      params.set('pageNumber', String(pageNumber));
      params.set('pageSize', String(pageSize));
      if (name) params.set('name', name);
      const raw = await AxiosService.get<{ message: string; code: number; result: { templates: tFetchTemplatesResponse['templates']; total_pages: number; current_page: number } }>(
        `/whatsapp-api/meta/${workspaceId}/templates?${params.toString()}`,
        options,
      );
      const normalized: tFetchTemplatesResponse = {
        message: raw.message,
        templates: raw.result?.templates ?? [],
        last_sent_template: { has_sent_template: false, conversation_expires_at: '', marketing_while_closed: 0, utility_while_closed: 0, marketing_while_open: 0, utility_while_open: 0 },
        undelivered_messages: { can_send_messages: true, undelivered_messages: 0, last_undelivered_message_at: '' },
      };
      return Promise.resolve(normalized);
    } catch (error) {
      return Promise.reject(error);
    }
  },
  getCampaignsStatistics: async (
    workspaceId: string,
  ): Promise<tGetCampaignsStatisticsResponse> => {
    try {
      return await AxiosService.get<tGetCampaignsStatisticsResponse>(
        `/whatsapp-api/workspaces/${workspaceId}/campaigns/statistics`,
      );
    } catch (error) {
      return Promise.reject(error);
    }
  },
  getCampaignMetrics: async (
    workspaceId: string,
    startDate: string,
    endDate: string,
  ): Promise<tCampaignMetricsResponse> => {
    try {
      const params = new URLSearchParams();
      params.set('start_date', startDate);
      params.set('end_date', endDate);
      return await AxiosService.get<tCampaignMetricsResponse>(
        `/whatsapp-api/workspaces/${workspaceId}/campaigns/metrics?${params.toString()}`,
      );
    } catch (error) {
      return Promise.reject(error);
    }
  },

  getDetailedMessagesStatistics: async function (
    workspaceId: string,
    startDate?: string,
    endDate?: string,
  ): Promise<tGetDetailedStatisticsResponse> {
    try {
      const params = new URLSearchParams();
      if (startDate) params.set('start_date', startDate);
      if (endDate) params.set('end_date', endDate);
      const query = params.toString();
      // INT-958: routed through bff-web (no isLegacy). Query forwarded verbatim.
      const url = `/whatsapp-api/workspaces/${workspaceId}/messaging/statistics/detailed${query ? `?${query}` : ''}`;

      return await AxiosService.get<tGetDetailedStatisticsResponse>(url);
    } catch (error) {
      return Promise.reject(error);
    }
  },

  getNotifications: async (
    workspaceId: string,
    options?: {
      limit?: number;
      cursor?: string;
      category?: tNotificationCategory;
      is_read?: boolean;
    },
  ): Promise<tGetNotificationsResponse> => {
    const params = new URLSearchParams();
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.cursor) params.set('cursor', options.cursor);
    if (options?.category) params.set('category', options.category);
    if (options?.is_read !== undefined)
      params.set('is_read', String(options.is_read));

    const query = params.toString();
    const url = `/whatsapp-api/account/${workspaceId}/notifications${query ? `?${query}` : ''}`;

    try {
      return await AxiosService.get<tGetNotificationsResponse>(url, {
        isLegacy: true,
      });
    } catch (error) {
      return Promise.reject(error);
    }
  },

  getUnreadNotificationCount: async (
    workspaceId: string,
  ): Promise<tGetUnreadCountResponse> => {
    try {
      return await AxiosService.get<tGetUnreadCountResponse>(
        `/whatsapp-api/account/${workspaceId}/notifications/count`,
        { isLegacy: true },
      );
    } catch (error) {
      return Promise.reject(error);
    }
  },

  markNotificationAsRead: async (
    workspaceId: string,
    notificationId: number,
  ): Promise<void> => {
    try {
      await AxiosService.post(
        `/whatsapp-api/account/${workspaceId}/notifications/${notificationId}/read`,
        {},
        { isLegacy: true },
      );
    } catch (error) {
      return Promise.reject(error);
    }
  },

  markAllNotificationsAsRead: async (
    workspaceId: string,
    before: string,
  ): Promise<void> => {
    try {
      await AxiosService.post(
        `/whatsapp-api/account/${workspaceId}/notifications/read-all?before=${encodeURIComponent(before)}`,
        {},
        { isLegacy: true },
      );
    } catch (error) {
      return Promise.reject(error);
    }
  },

  // BUG-1074: `audience` asks for each stage's `leads_count` as a count under
  // that rule (see tLeadsAudience), not as the stage's stored counter.
  getFunnelsSummary: async (
    workspaceId: string,
    options: { userId: string; audience: tLeadsAudience },
  ): Promise<tGetFunnelsSummaryResponse> => {
    try {
      return await AxiosService.get<tGetFunnelsSummaryResponse>(
        `/leads/ws/${workspaceId}/funnels`,
        {
          isLegacy: true,
          headers: { 'Auth-id': options.userId },
          params: { audience: options.audience },
        },
      );
    } catch (error) {
      return Promise.reject(error);
    }
  },

  getCampaignDetail: async (
    workspaceId: string,
    campaignId: string,
  ): Promise<tGetCampaignDetailResponse> => {
    try {
      return await AxiosService.get<tGetCampaignDetailResponse>(
        `/whatsapp-api/workspaces/${workspaceId}/campaigns/${campaignId}`,
      );
    } catch (error) {
      return Promise.reject(error);
    }
  },

  /**
   * Soft-deletes a draft campaign. The BFF forwards to whatsapp-business-service
   * which enforces the draft-only guard. Non-draft campaigns return 422.
   * @param workspaceId Workspace ID.
   * @param campaignId Campaign ID (numeric).
   */
  deleteCampaign: async (
    workspaceId: string,
    campaignId: string | number,
  ): Promise<tDeleteCampaignResponse> => {
    try {
      return await AxiosService.delete<tDeleteCampaignResponse>(
        `/whatsapp-api/workspaces/${workspaceId}/campaigns/${campaignId}`,
      );
    } catch (error) {
      return Promise.reject(error);
    }
  },

  /**
   * Cancels a scheduled campaign. The BFF forwards to whatsapp-business-service
   * which enforces the scheduled-only guard. Non-scheduled campaigns return 422.
   * @param workspaceId Workspace ID.
   * @param campaignId Campaign ID (numeric).
   * @param userUuid Workspace user UUID of the actor performing the cancel.
   *   Stored in `campaigns.cancelled_by` to mirror the `user_id` audit trail
   *   that campaign creation persists. Comes from `useUserContext().currentUser.uuid`
   *   in the FE — NOT the Firebase auth_id (those are different IDs;
   *   `campaigns.user_id` and `cancelled_by` both reference `users.uuid`).
   */
  cancelCampaign: async (
    workspaceId: string,
    campaignId: string | number,
    userUuid: string,
  ): Promise<tCancelCampaignResponse> => {
    try {
      return await AxiosService.post<{ user_uuid: string }, tCancelCampaignResponse>(
        `/whatsapp-api/workspaces/${workspaceId}/campaigns/${campaignId}/cancel`,
        { user_uuid: userUuid },
      );
    } catch (error) {
      return Promise.reject(error);
    }
  },

  createCampaign: async (
    workspaceId: string,
    request: tCreateCampaignRequest,
  ): Promise<tCreateCampaignResponse> => {
    try {
      return await AxiosService.post<tCreateCampaignRequest, tCreateCampaignResponse>(
        `/whatsapp-api/campaigns/${workspaceId}`,
        request,
        { isLegacy: true },
      );
    } catch (error) {
      return Promise.reject(error);
    }
  },

  /**
   * INT-1136: read-only preview of campaign recipients at risk of spam
   * blocking (closed 24h window + >= 1 template already sent while closed).
   * Takes the same audience selection as createCampaign; never creates
   * contacts or campaign rows.
   */
  getCampaignAtRiskRecipients: async (
    workspaceId: string,
    request: tCampaignAtRiskPreviewRequest,
  ): Promise<tGetCampaignAtRiskPreviewResponse> => {
    try {
      return await AxiosService.post<tCampaignAtRiskPreviewRequest, tGetCampaignAtRiskPreviewResponse>(
        `/whatsapp-api/campaigns/${workspaceId}/at-risk-recipients`,
        request,
        { isLegacy: true },
      );
    } catch {
      // The preview is informational only: on any error resolve with a
      // zeroed result so callers never block the send flow on this call.
      return {
        message: 'at-risk preview unavailable',
        code: 0,
        result: { requested: 0, at_risk_count: 0, at_risk_recipients: [] },
      };
    }
  },

  /**
   * INT-1221: read-only preview of which recipients of the selected audience
   * are missing a value for a predefined ("Auto") variable the given
   * template uses. Same fail-open posture as getCampaignAtRiskRecipients: a
   * network error resolves to a zeroed result rather than blocking the modal.
   */
  getCampaignMissingVariablesPreview: async (
    workspaceId: string,
    request: tCampaignMissingVariablesPreviewRequest,
  ): Promise<tGetCampaignMissingVariablesPreviewResponse> => {
    try {
      return await AxiosService.post<
        tCampaignMissingVariablesPreviewRequest,
        tGetCampaignMissingVariablesPreviewResponse
      >(
        `/whatsapp-api/campaigns/${workspaceId}/missing-variables-preview`,
        request,
        { isLegacy: true },
      );
    } catch {
      return {
        message: 'missing variables preview unavailable',
        code: 0,
        result: { requested: 0, pending_count: 0, pending_recipients: [] },
      };
    }
  },

  createDraftCampaign: async (
    workspaceId: string,
    request: tCreateDraftCampaignRequest,
  ): Promise<tCreateDraftCampaignResponse> => {
    try {
      return await AxiosService.post<tCreateDraftCampaignRequest, tCreateDraftCampaignResponse>(
        `/whatsapp-api/campaigns/${workspaceId}/draft`,
        request,
        { isLegacy: true },
      );
    } catch (error) {
      return Promise.reject(error);
    }
  },

  resendCampaign: async (
    workspaceId: string,
    campaignId: string,
    request: tResendCampaignRequest,
  ): Promise<tResendCampaignResponse> => {
    try {
      return await AxiosService.post<tResendCampaignRequest, tResendCampaignResponse>(
        `/whatsapp-api/workspaces/${workspaceId}/campaigns/${campaignId}/resend`,
        request,
      );
    } catch (error) {
      return Promise.reject(error);
    }
  },

  updateResendSchedule: async (
    workspaceId: string,
    campaignId: string,
    request: tResendCampaignRequest,
  ): Promise<tResendCampaignResponse> => {
    try {
      return await AxiosService.patch<tResendCampaignRequest, tResendCampaignResponse>(
        `/whatsapp-api/workspaces/${workspaceId}/campaigns/${campaignId}/resend`,
        request,
      );
    } catch (error) {
      return Promise.reject(error);
    }
  },

  getCampaignRecord: async (
    workspaceId: string,
    campaignId: string,
  ): Promise<tGetCampaignRecordResponse> => {
    try {
      return await AxiosService.get<tGetCampaignRecordResponse>(
        `/whatsapp-api/workspaces/${workspaceId}/campaigns/${campaignId}/record`,
      );
    } catch (error) {
      return Promise.reject(error);
    }
  },

  getUndeliveredLeads: async (
    workspaceId: string,
    campaignId: string,
    page: number = 1,
    pageSize: number = 20,
    failureCodes?: tFailureCodeFilterValue[],
  ): Promise<tGetUndeliveredLeadsResponse> => {
    try {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('pageSize', String(pageSize));
      if (failureCodes && failureCodes.length > 0) {
        failureCodes.forEach((code) => params.append('failureCode[]', String(code)));
      }
      return await AxiosService.get<tGetUndeliveredLeadsResponse>(
        `/whatsapp-api/workspaces/${workspaceId}/campaigns/${campaignId}/undelivered-leads?${params.toString()}`,
      );
    } catch (error) {
      return Promise.reject(error);
    }
  },

  archiveLead: async (
    workspaceId: string,
    leadId: string,
  ): Promise<void> => {
    try {
      await AxiosService.post(`/workspace/${workspaceId}/leads/${leadId}/archive`, undefined);
    } catch (error) {
      return Promise.reject(error);
    }
  },

  bulkArchiveLeads: async (
    workspaceId: string,
    leadIds: string[],
  ): Promise<void> => {
    try {
      await AxiosService.post(`/workspace/${workspaceId}/leads/bulk-archive`, { leads: leadIds });
    } catch (error) {
      return Promise.reject(error);
    }
  },

  detachTagFromLead: async (
    workspaceId: string,
    leadId: string,
    tagName: string,
  ): Promise<void> => {
    try {
      await AxiosService.put(`/ws/${workspaceId}/lds/${leadId}`, { detach_tags: [tagName] }, { isLegacy: true });
    } catch (error) {
      return Promise.reject(error);
    }
  },

  attachTagToLead: async (
    workspaceId: string,
    leadId: string,
    tagName: string,
  ): Promise<void> => {
    try {
      await AxiosService.put(`/ws/${workspaceId}/lds/${leadId}`, { attach_tags: [tagName] }, { isLegacy: true });
    } catch (error) {
      return Promise.reject(error);
    }
  },

  getWorkspaceTags: async (
    workspaceId: string,
  ): Promise<string[]> => {
    try {
      return await AxiosService.get<string[]>(`/ws/${workspaceId}/tags`, { isLegacy: true });
    } catch (error) {
      return Promise.reject(error);
    }
  },

  // BUG-1074: `audience` asks for each tag's `leads_count` under that rule
  // (see tLeadsAudience). The Directory's own tag count sends no audience.
  getTagsWithCount: async (
    workspaceId: string,
    options: { audience: tLeadsAudience },
  ): Promise<tTagsWithCountResponse> => {
    try {
      return await AxiosService.get<tTagsWithCountResponse>(
        `/workspace/${workspaceId}/tags/count`,
        { params: { audience: options.audience } },
      );
    } catch (error) {
      return Promise.reject(error);
    }
  },
};
